'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  phone: string;
  inquiryType: string;
  message: string;
  rating: number;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  rating?: string;
}

const ContactForm = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    inquiryType: 'general',
    message: '',
    rating: 0,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [rateLimitRetryAfter, setRateLimitRetryAfter] = useState<number | null>(null);

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'catering', label: 'Catering Request' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'complaint', label: 'Complaint' },
  ];

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return undefined;
      case 'email': {
        if (!value.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        return undefined;
      }
      case 'phone': {
        if (!value.trim()) return 'Phone number is required';
        const phoneRegex = /^\+?1?\s*\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
        if (!phoneRegex.test(value)) return 'Please enter a valid phone number';
        return undefined;
      }
      case 'message':
        if (!value.trim()) return 'Message is required';
        if (value.trim().length < 10) return 'Message must be at least 10 characters';
        return undefined;
      case 'rating':
        if (formData.inquiryType === 'feedback' && (Number(value) < 1 || Number(value) > 5))
          return 'Please select a rating (1–5 stars)';
        return undefined;
      default:
        return undefined;
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const next = { ...formData, [name]: name === 'rating' ? Number(value) : value };
    if (name === 'inquiryType' && value !== 'feedback') next.rating = 0;
    setFormData(next);

    if (touched[name]) {
      const error = validateField(name, String(value));
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const setRating = (stars: number) => {
    if (formData.inquiryType !== 'feedback') return;
    setFormData((prev) => ({ ...prev, rating: stars }));
    setErrors((prev) => ({ ...prev, rating: undefined }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setRateLimitRetryAfter(null);

    const newErrors: FormErrors = {};
    newErrors.name = validateField('name', formData.name);
    newErrors.email = validateField('email', formData.email);
    newErrors.phone = validateField('phone', formData.phone);
    newErrors.message = validateField('message', formData.message);
    if (formData.inquiryType === 'feedback') {
      newErrors.rating = formData.rating < 1 || formData.rating > 5
        ? 'Please select a rating (1–5 stars)'
        : undefined;
    }

    const hasErrors = Object.values(newErrors).some((error) => error !== undefined);

    if (hasErrors) {
      setErrors(newErrors);
      setTouched({ name: true, email: true, phone: true, message: true, rating: true });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          inquiryType: formData.inquiryType,
          message: formData.message,
          ...(formData.inquiryType === 'feedback' && formData.rating >= 1 && formData.rating <= 5 && { rating: formData.rating }),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 429) {
        setSubmitError(data.error || 'Too many attempts. Please wait a moment before trying again.');
        setRateLimitRetryAfter(data.retryAfterSeconds ?? 10);
        return;
      }

      if (!response.ok) {
        setSubmitError(data.error || 'Unable to send your message. Please try again.');
        return;
      }

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        inquiryType: 'general',
        message: '',
        rating: 0,
      });
      setErrors({});
      setTouched({});
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch {
      setSubmitError('Something went wrong. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const characterCount = formData.message.length;
  const maxCharacters = 500;

  return (
    <div className="rounded-lg bg-card shadow-md">
      <div className="border-b border-border px-6 py-4">
        <h2 className="font-heading text-xl font-semibold text-foreground">Send Us a Message</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Fill out the form below and we'll get back to you as soon as possible.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 px-6 py-6">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="text-caption mb-2 block text-foreground">
            Full Name <span className="text-error">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full rounded-lg border px-4 py-3 text-sm transition-smooth focus-ring ${
              errors.name && touched.name
                ? 'border-error bg-error/5'
                : 'border-border bg-background'
            }`}
            placeholder="Durga Shankar"
            disabled={isSubmitting}
          />
          {errors.name && touched.name && (
            <p className="mt-1 flex items-center space-x-1 text-xs text-error">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.name}</span>
            </p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="text-caption mb-2 block text-foreground">
            Email Address <span className="text-error">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full rounded-lg border px-4 py-3 text-sm transition-smooth focus-ring ${
              errors.email && touched.email
                ? 'border-error bg-error/5'
                : 'border-border bg-background'
            }`}
            placeholder="durga@example.com"
            disabled={isSubmitting}
          />
          {errors.email && touched.email && (
            <p className="mt-1 flex items-center space-x-1 text-xs text-error">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.email}</span>
            </p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <label htmlFor="phone" className="text-caption mb-2 block text-foreground">
            Phone Number <span className="text-error">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full rounded-lg border px-4 py-3 text-sm transition-smooth focus-ring ${
              errors.phone && touched.phone
                ? 'border-error bg-error/5'
                : 'border-border bg-background'
            }`}
            placeholder="+91 75420 72555"
            disabled={isSubmitting}
          />
          {errors.phone && touched.phone && (
            <p className="mt-1 flex items-center space-x-1 text-xs text-error">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.phone}</span>
            </p>
          )}
        </div>

        {/* Inquiry Type Dropdown */}
        <div>
          <label htmlFor="inquiryType" className="text-caption mb-2 block text-foreground">
            Inquiry Type
          </label>
          <select
            id="inquiryType"
            name="inquiryType"
            value={formData.inquiryType}
            onChange={handleChange}
            className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm transition-smooth focus-ring"
            disabled={isSubmitting}
          >
            {inquiryTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Rating (1–5 stars) when Inquiry Type is Feedback */}
        {formData.inquiryType === 'feedback' && (
          <div>
            <label className="text-caption mb-2 block text-foreground">
              Rating <span className="text-error">*</span>
            </label>
            <div className="flex items-center gap-1" role="slider" aria-valuemin={1} aria-valuemax={5} aria-valuenow={formData.rating} aria-valuetext={`${formData.rating} out of 5 stars`} tabIndex={0}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setRating(star); }}
                  className="p-1 rounded focus:outline-none focus-ring-2 focus-ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
                  disabled={isSubmitting}
                  aria-label={`${star} star${star > 1 ? 's' : ''}`}
                >
                  <Icon
                    name="StarIcon"
                    size={28}
                    className={formData.rating >= star ? 'text-warning fill-warning' : 'text-muted-foreground'}
                  />
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Click to rate from 1 to 5 stars</p>
            {errors.rating && touched.rating && (
              <p className="mt-1 flex items-center space-x-1 text-xs text-error">
                <Icon name="ExclamationCircleIcon" size={14} />
                <span>{errors.rating}</span>
              </p>
            )}
          </div>
        )}

        {/* Message Textarea */}
        <div>
          <label htmlFor="message" className="text-caption mb-2 block text-foreground">
            Message <span className="text-error">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={6}
            maxLength={maxCharacters}
            className={`w-full rounded-lg border px-4 py-3 text-sm transition-smooth focus-ring ${
              errors.message && touched.message
                ? 'border-error bg-error/5'
                : 'border-border bg-background'
            }`}
            placeholder="Tell us how we can help you..."
            disabled={isSubmitting}
          />
          <div className="mt-1 flex items-center justify-between">
            {errors.message && touched.message ? (
              <p className="flex items-center space-x-1 text-xs text-error">
                <Icon name="ExclamationCircleIcon" size={14} />
                <span>{errors.message}</span>
              </p>
            ) : (
              <span></span>
            )}
            <span
              className={`text-xs ${
                characterCount > maxCharacters * 0.9 ? 'text-warning' : 'text-muted-foreground'
              }`}
            >
              {characterCount}/{maxCharacters}
            </span>
          </div>
        </div>

        {/* Success Message */}
        {submitSuccess && (
          <div className="flex items-center space-x-2 rounded-lg bg-success/10 px-4 py-3 text-success">
            <Icon name="CheckCircleIcon" size={20} />
            <span className="text-sm font-medium">
              Thank you! Your message has been sent successfully.
            </span>
          </div>
        )}

        {/* Error / Rate limit Message */}
        {submitError && (
          <div className="flex items-center space-x-2 rounded-lg bg-error/10 px-4 py-3 text-error">
            <Icon name="ExclamationCircleIcon" size={20} />
            <span className="text-sm font-medium">
              {submitError}
              {rateLimitRetryAfter != null && (
                <span className="block mt-1 text-xs">You can try again in {rateLimitRetryAfter} seconds.</span>
              )}
            </span>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-primary px-6 py-3 font-semibold text-primary-foreground transition-smooth hover:bg-primary/90 focus-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              <span>Sending...</span>
            </>
          ) : (
            <>
              <Icon name="PaperAirplaneIcon" size={20} />
              <span>Send Message</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
